import { cloneElement, forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type MouseEvent, type ReactElement, type ReactNode } from 'react'
import { Layers, ListOrdered, MapPin, Megaphone, Mic, MicOff, MonitorPlay, Search, UserMinus, Users, X } from 'lucide-react'
import { Autocomplete, Avatar, Button, IconButton, Popover, type IconButtonProps } from '@/components/play'
import { matchesAthlete } from '@/lib/watch/athleteSearch'
import type { CohostConsole, CohostStatus } from '@/lib/watch/cohostActions'
import { COHOST_CLEAR_METHOD, type CohostDone } from '@/lib/watch/cohostHandlers'
import type { EventAthlete } from '@/lib/watch/eventAthletes'
import { previewGraphic, type GraphicSlot, type RaceGender, type StageGraphicInput } from '@/lib/watch/graphics'
import { genderWord, raceById, standingsTitle, type Race } from '@/lib/watch/races'
import type { StageActionResult, StageActions } from '@/lib/watch/stageActions'
import { useWatchLayout } from '@/lib/watch/useMediaQuery'
import type { VisibleGraphics } from '@/lib/watch/useStageGraphics'
import { cn } from '@/lib/utils'
import { BadgerGlyph } from '../BadgerAvatar'
import { WatchSheet } from '../WatchSheet'
import type { GraphicPreview } from './BroadcastLayer'
import { GENDER_GLYPH } from './glyphs'
import { Bib, Portrait, RaceTag } from './primitives'
import './console.css'

/** How many athletes the search lists; typing more narrows it. */
export const CONSOLE_LISTS_UP_TO = 8
/** A quick pick's rows: a quiet five over the video. */
export const QUICK_PICK_ROWS = 5

const GENDERS: readonly (RaceGender | null)[] = [null, 'female', 'male']
/** How long a board's preview holds after the pointer or focus leaves its pick, so moving to the next one doesn't blink. */
const BOARD_LEAVE_MS = 150

export interface GraphicsConsoleProps {
  /** The public roster the page holds. */
  roster: readonly EventAthlete[]
  races: readonly Race[]
  /** What the stage's audience sees now (`useStageGraphics`). */
  onScreen: VisibleGraphics
  actions: StageActions
  /** Why Map can't run (the event has no course map), or null. */
  mapUnavailable: string | null
  /** The host's own preview on their canvas, or null to end it. */
  onPreview(preview: GraphicPreview | null): void
  /** Badger on this stage (`useCohostConsole`), or null to leave his row out. */
  badger?: CohostConsole | null
  /** What Badger last did through this host's page (`useCohostHandlers`), said in the status line as it happens. */
  badgerDid?: CohostDone | null
}

const athletePreview = (athleteId: string): GraphicPreview => ({ slot: 'lower_third', spec: previewGraphic({ kind: 'athlete', athleteId }) })

function boardInput(race: Race, gender: RaceGender | null): StageGraphicInput & { kind: 'standings' } {
  return { kind: 'standings', routeId: race.id, gender, limit: QUICK_PICK_ROWS }
}

/** A search row: photo or initials, name, then bib and race. */
function AthleteOption({ athlete, race }: { athlete: EventAthlete; race: Race | null }) {
  return (
    <>
      <Avatar name={athlete.name} src={athlete.photoUrl} size="sm" tone="auto" toneKey={athlete.id} />
      <span className="bc-console__option">
        <span className="bc-console__option-name">{athlete.name}</span>
        {(athlete.bib || race) && (
          <span className="bc-console__option-meta">
            {athlete.bib && <Bib bib={athlete.bib} />}
            {race && <RaceTag race={race} />}
          </span>
        )}
      </span>
    </>
  )
}

/**
 * A section of the console, icons over words: its glyph marks it and its title names it for assistive technology. The
 * athletes' section needs no glyph: its search field carries one.
 */
function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  const id = useId()
  return (
    <section className="bc-console__section" aria-labelledby={id}>
      <h3 id={id} className={icon ? 'bc-console__label' : 'sr-only'}>
        {icon}
        <span className="sr-only">{title}</span>
      </h3>
      {children}
    </section>
  )
}

/** A quick pick's glyph: everyone, or Venus or Mars. */
function GenderGlyph({ gender }: { gender: RaceGender | null }) {
  const Glyph = gender ? GENDER_GLYPH[gender] : Users
  return <Glyph aria-hidden="true" />
}

/**
 * The host's graphics console (docs/features/badger-cohost.md, The host console): **Athletes**, a search over the roster
 * the page holds, where the highlighted athlete's card previews on the host's own canvas (marked Preview) and a chosen
 * one can go on air (Show) or on the map (Map); **Leaderboards**, each race's quick picks for everyone, women and men,
 * previewed on hover or focus and shown on a press; **On screen**, what each slot shows the audience now, with Clear;
 * **Badger** (`BadgerRow`), bringing him in, muting him and sending him off. Every graphics button runs the stage's one
 * set of actions (`stageActions.ts`), which Badger shares over RPC (`cohostHandlers.ts`): what he does shows in
 * the status line after his face (`badgerDid`). His own actions run through `cohostActions.ts`.
 */
export function GraphicsConsole({ roster, races, onScreen, actions, mapUnavailable, onPreview, badger = null, badgerDid = null }: GraphicsConsoleProps) {
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState<EventAthlete | null>(null)
  const [picked, setPicked] = useState<EventAthlete | null>(null)
  // The picked athlete's card previews until it goes on air.
  const [pickedShown, setPickedShown] = useState(false)
  const [board, setBoard] = useState<GraphicPreview | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // `until`: Badger's status that outdates the line ("Badger is joining" once he is here). `byBadger`: he did it.
  const [message, setMessage] = useState<{ tone: 'done' | 'failed'; text: string; until?: CohostStatus; byBadger?: boolean } | null>(null)
  const outdated = message?.until !== undefined && message.until === badger?.status
  useEffect(() => {
    if (outdated) setMessage(null)
  }, [outdated])
  // What Badger does while the console is open takes the line, as the host's own actions do; what he did before it
  // opened is old news (On screen says what is up now).
  const badgerSeen = useRef(badgerDid)
  useEffect(() => {
    if (!badgerDid || badgerDid === badgerSeen.current) return
    badgerSeen.current = badgerDid
    const { method, result } = badgerDid
    setMessage(
      result.ok
        ? { tone: 'done', text: method === COHOST_CLEAR_METHOD ? result.shown : `Showing ${result.shown}`, byBadger: true }
        : { tone: 'failed', text: result.reason, byBadger: true },
    )
  }, [badgerDid])
  const showRef = useRef<HTMLButtonElement>(null)

  const matches = useMemo(
    () => (query.trim() ? roster.filter((athlete) => matchesAthlete(athlete, query)).slice(0, CONSOLE_LISTS_UP_TO) : []),
    [roster, query],
  )

  // The preview on the host's canvas: what is highlighted or hovered first, else the picked athlete until shown.
  const preview = highlighted
    ? athletePreview(highlighted.id)
    : board ?? (picked && !pickedShown ? athletePreview(picked.id) : null)
  const previewKey = preview ? JSON.stringify(preview) : null
  const previewRef = useRef(onPreview)
  previewRef.current = onPreview
  useEffect(() => {
    previewRef.current(preview)
    // The key says everything the preview holds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey])
  // Closing the console ends its preview.
  useEffect(() => () => previewRef.current(null), [])

  const run = useCallback<RunAction>(async (key, action, done = (shown) => `Showing ${shown}`, until) => {
    setBusy(key)
    setMessage(null)
    try {
      const result = await action()
      setMessage(result.ok ? { tone: 'done', text: done(result.shown), until } : { tone: 'failed', text: result.reason })
      return result
    } finally {
      setBusy(null)
    }
  }, [])

  // A board's preview while the pointer or focus is on its pick; moving from one pick to the next doesn't drop it between.
  const leaving = useRef(0)
  const previewBoard = (next: GraphicPreview | null) => {
    window.clearTimeout(leaving.current)
    if (next) setBoard(next)
    else leaving.current = window.setTimeout(() => setBoard(null), BOARD_LEAVE_MS)
  }
  useEffect(() => () => window.clearTimeout(leaving.current), [])

  const choose = (athlete: EventAthlete) => {
    setPicked(athlete)
    setPickedShown(false)
    setHighlighted(null)
    // Enter on the search, then Enter again, puts them on air.
    requestAnimationFrame(() => showRef.current?.focus())
  }

  const showPicked = async () => {
    if (!picked) return
    const result = await run('athlete', () => actions.showAthlete(picked.id))
    if (result.ok) setPickedShown(true)
  }
  const mapPicked = async () => {
    if (!picked) return
    const result = await run('map', () => actions.showOnMap(picked.id))
    if (result.ok) setPickedShown(true)
  }
  const showBoard = async (race: Race, gender: RaceGender | null) => {
    const input = boardInput(race, gender)
    const result = await run(`board:${race.id}:${gender ?? 'all'}`, () => actions.showStandings(input))
    if (result.ok) previewBoard(null)
  }
  const clear = (slot: GraphicSlot) => run(`clear:${slot}`, () => actions.clearGraphic(slot), () => 'Cleared')

  const pickedRace = picked ? raceById(races, picked.routeId) : null
  const lowerOnAir = onScreen.lowerThird
  const lowerAthlete = lowerOnAir ? roster.find((athlete) => athlete.id === lowerOnAir.athleteId) : undefined
  const panelOnAir = onScreen.panel
  const panelRace = panelOnAir ? raceById(races, panelOnAir.routeId) : null

  return (
    <div className="bc-console">
      <Section title="Athletes">
        <div
          className="bc-console__search"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHighlighted(null)
          }}
        >
          <Autocomplete
            label="Find an athlete"
            hideLabel
            size="compact"
            icon={<Search />}
            placeholder="Bib or name"
            value={query}
            onValueChange={(next) => {
              setQuery(next)
              if (!next.trim()) setHighlighted(null)
            }}
            items={matches}
            itemLabel={(athlete) => athlete.name}
            itemKey={(athlete) => athlete.id}
            keepValue
            onItemChoose={choose}
            onItemHighlight={setHighlighted}
            renderItem={(athlete) => <AthleteOption athlete={athlete} race={raceById(races, athlete.routeId)} />}
            empty={roster.length ? 'No athlete matches' : 'No athletes yet'}
          />
        </div>
        {picked && (
          <div className="bc-console__picked">
            <Portrait athlete={picked} size="row" />
            <span className="bc-console__picked-name">
              <span className="bc-console__option-name">{picked.name}</span>
              {(picked.bib || pickedRace) && (
                <span className="bc-console__option-meta">
                  {picked.bib && <Bib bib={picked.bib} />}
                  {pickedRace && <RaceTag race={pickedRace} />}
                </span>
              )}
            </span>
            <Button ref={showRef} size="compact" loading={busy === 'athlete'} onClick={() => void showPicked()}>
              <Megaphone aria-hidden="true" />
              Show
            </Button>
            <IconButton
              label={mapUnavailable ? `Show on map (${mapUnavailable})` : 'Show on map'}
              size="compact"
              variant="secondary"
              disabled={Boolean(mapUnavailable)}
              loading={busy === 'map'}
              focusableWhenDisabled
              onClick={() => void mapPicked()}
            >
              <MapPin />
            </IconButton>
          </div>
        )}
      </Section>

      <Section title="Leaderboards" icon={<ListOrdered aria-hidden="true" />}>
        {races.length === 0 ? (
          <p className="bc-console__quiet">No races on this event</p>
        ) : (
          <ul className="bc-console__boards">
            {races.map((race) => (
              <li key={race.id} className="bc-console__board">
                <RaceTag race={race} className="bc-console__board-race" />
                <span className="bc-console__picks">
                  {GENDERS.map((gender) => {
                    const preview: GraphicPreview = { slot: 'panel', spec: previewGraphic(boardInput(race, gender)) }
                    const key = `board:${race.id}:${gender ?? 'all'}`
                    return (
                      <IconButton
                        key={gender ?? 'all'}
                        label={`${genderWord(gender)} · ${race.label}`}
                        size="compact"
                        variant="secondary"
                        loading={busy === key}
                        onPointerEnter={() => previewBoard(preview)}
                        onPointerLeave={() => previewBoard(null)}
                        onFocus={() => previewBoard(preview)}
                        onBlur={() => previewBoard(null)}
                        onClick={() => void showBoard(race, gender)}
                      >
                        <GenderGlyph gender={gender} />
                      </IconButton>
                    )
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="On screen" icon={<MonitorPlay aria-hidden="true" />}>
        {!lowerOnAir && !panelOnAir ? (
          <p className="bc-console__quiet">Nothing on screen</p>
        ) : (
          <ul className="bc-console__on-air">
            {lowerOnAir && (
              <li className="bc-console__aired">
                {lowerAthlete ? <Portrait athlete={lowerAthlete} size="line" /> : null}
                <span className="bc-console__aired-name">{lowerAthlete?.name ?? 'Athlete card'}</span>
                <IconButton
                  label="Clear the athlete card"
                  size="compact"
                  variant="ghost"
                  disabled={busy === 'clear:lower_third'}
                  onClick={() => void clear('lower_third')}
                >
                  <X />
                </IconButton>
              </li>
            )}
            {panelOnAir && (
              <li className="bc-console__aired">
                {panelOnAir.gender && <GenderGlyph gender={panelOnAir.gender} />}
                {panelRace && <span className="bc-race__swatch" style={{ background: panelRace.color }} aria-hidden="true" />}
                <span className="bc-console__aired-name">
                  {panelRace ? (
                    panelOnAir.title || (
                      <>
                        <span aria-hidden="true">{panelRace.label}</span>
                        <span className="sr-only">{standingsTitle(panelRace, panelOnAir.gender)}</span>
                      </>
                    )
                  ) : (
                    'Leaderboard'
                  )}
                </span>
                <IconButton label="Clear the leaderboard" size="compact" variant="ghost" disabled={busy === 'clear:panel'} onClick={() => void clear('panel')}>
                  <X />
                </IconButton>
              </li>
            )}
          </ul>
        )}
      </Section>

      {badger && <BadgerRow badger={badger} busy={busy} run={run} />}

      {/* Badger's doing is his face before the words; "Badger:" is for assistive technology. */}
      <p className={cn('bc-console__message', message?.tone === 'failed' && 'bc-console__message--failed')} role="status" aria-live="polite">
        {outdated || !message ? null : message.byBadger ? (
          <>
            <BadgerGlyph />
            <span className="sr-only">Badger: </span>
            {message.text}
          </>
        ) : (
          message.text
        )}
      </p>
    </div>
  )
}

/**
 * Runs a console action under `key` (its button shows busy meanwhile) and says what it did, or why not, in the status line,
 * until Badger reaches `until`.
 */
type RunAction = (key: string, action: () => Promise<StageActionResult>, done?: (shown: string) => string, until?: CohostStatus) => Promise<StageActionResult>

const said = (shown: string) => shown

/**
 * Badger's row (docs/features/badger-cohost.md, The host console), icons over words: his listening frame is the glyph,
 * the words are the buttons' names and tooltips. Not asked for: **Bring in Badger**. Asked for and not in the room yet:
 * **Badger is joining…**, busy, with Send off to call it off. In the room: his face, **Mute** (a toggle, pressed while
 * his track is muted) and **Send off**. Sent off and still in the room: **Badger is leaving…**, busy. What each did, or
 * why not, is said in the console's status line, as the graphics' actions are.
 */
function BadgerRow({ badger, busy, run }: { badger: CohostConsole; busy: string | null; run: RunAction }) {
  const { status, muted, actions } = badger
  const row = useFocusKept<HTMLDivElement>()
  const first = status === 'off' ? 'Bring in Badger' : status === 'joining' ? 'Badger is joining…' : 'Badger is leaving…'
  return (
    <Section title="Badger">
      <div ref={row.ref} className="bc-console__badger" data-status={status} onFocus={row.onFocus} onBlur={row.onBlur}>
        {status === 'present' ? (
          <span className="bc-console__badger-face">
            <BadgerGlyph />
          </span>
        ) : (
          // One button from Bring in to joining (and from leaving back to Bring in), so focus stays on it.
          <IconButton
            label={first}
            size="compact"
            variant="secondary"
            loading={status !== 'off' || busy === 'badger:in'}
            onClick={status === 'off' ? () => void run('badger:in', actions.bringIn, said, 'present') : undefined}
          >
            <BadgerGlyph />
          </IconButton>
        )}
        {status === 'present' && (
          <IconButton
            label="Mute Badger"
            aria-pressed={muted}
            size="compact"
            variant="secondary"
            loading={busy === 'badger:mute'}
            onClick={() => void run('badger:mute', () => actions.setMuted(!muted), said)}
          >
            {muted ? <MicOff /> : <Mic />}
          </IconButton>
        )}
        {(status === 'present' || status === 'joining') && (
          <IconButton label="Send Badger off" size="compact" variant="ghost" loading={busy === 'badger:off'} onClick={() => void run('badger:off', actions.sendOff, said, 'off')}>
            <UserMinus />
          </IconButton>
        )}
      </div>
    </Section>
  )
}

/**
 * Keeps keyboard focus in a row whose buttons come and go with the state it shows (Badger joining, then here): React
 * fires no blur for a removed button, so after a render that took the focused one away, with nothing else focused,
 * focus goes to the row's first button.
 */
function useFocusKept<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const inside = useRef(false)
  useLayoutEffect(() => {
    const row = ref.current
    if (!inside.current || !row || row.contains(document.activeElement)) return
    const active = document.activeElement
    if (active && active !== document.body) {
      inside.current = false
      return
    }
    row.querySelector<HTMLElement>('button')?.focus({ preventScroll: true })
  })
  return {
    ref,
    onFocus: () => {
      inside.current = true
    },
    onBlur: (event: FocusEvent<T>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) inside.current = false
    },
  }
}

export interface GraphicsConsoleOpenerProps extends GraphicsConsoleProps {
  /** Its trigger in the dock, named Graphics (`GraphicsButton` by default). */
  trigger?: ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>
}

/**
 * The dock's Graphics button: the Lucide layers glyph, named with a tooltip. It forwards its ref and props, so the
 * popover can anchor to it and toggle it.
 */
export const GraphicsButton = forwardRef<HTMLButtonElement, Omit<IconButtonProps, 'label' | 'children'>>(function GraphicsButton(props, ref) {
  return (
    <IconButton ref={ref} variant="ghost" label="Graphics" {...props}>
      <Layers />
    </IconButton>
  )
})

/**
 * Opens the console from the host's dock, as Invite to stage opens: a Play Popover above the button on the desktop, a
 * sheet from the bottom on a phone. It mounts only while open, so its preview ends as it closes.
 */
export function GraphicsConsoleOpener({ trigger = <GraphicsButton />, ...props }: GraphicsConsoleOpenerProps) {
  const desktop = useWatchLayout() === 'desktop'
  const [open, setOpen] = useState(false)
  const panel = <GraphicsConsole {...props} />

  if (desktop) {
    return (
      <Popover title="Graphics" trigger={trigger} side="top" align="center" open={open} onOpenChange={setOpen} className="bc-console-popover">
        {panel}
      </Popover>
    )
  }

  const opener = cloneElement(trigger, {
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    onClick: (event: MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event)
      setOpen(true)
    },
  } as Record<string, unknown>)
  return (
    <>
      {opener}
      <WatchSheet open={open} onOpenChange={setOpen} title="Graphics" className="bc-console-sheet">
        {panel}
      </WatchSheet>
    </>
  )
}
